import React from "react";
import { Box, Divider, Typography, Grid } from "@mui/joy";
import { Assignment, PostalAddress } from "../../../api/assignments/Assignments.domain";

interface CommonReadOnlyLayoutProps {
  assignment: Assignment;
}

const CommonReadOnlyLayout: React.FC<CommonReadOnlyLayoutProps> = ({ assignment }) => {
  const damagedObject = assignment.damagedManufacturedObject;

  return (
    <Box display="flex" flexDirection="column" gap={2} sx={{ width: "100%" }}>
      <Box component="section">
        <Typography level="title-lg" mb={1}>
          Identifiers
        </Typography>
        <Field label="Assignment ID" value={assignment.assignmentId} />
        <Field label="Benefit ID" value={assignment.benefitId} />
        <OptionalField label="Parent Benefit ID" value={assignment.parentBenefitId} />
        <OptionalField label="Claim ID" value={assignment.claimId} />
        <OptionalField label="External Assignment ID" value={assignment.externalAssignmentId} />
        <OptionalField label="Issue Date" value={assignment.issueDate} />
        <OptionalField label="Accepted SP Code" value={assignment.acceptedServicePointCode} />
        <OptionalField label="Status" value={assignment.assignmentStatus} />
        <OptionalField label="Last Update Date" value={assignment.lastUpdateDate} />
      </Box>
      <Divider />

      {assignment.eventType && (
        <Box component="section">
          <Typography level="title-lg" mb={1}>
            Event
          </Typography>
          <Field label="Event Type" value={assignment.eventType.code} />
          <OptionalField label="Event Label" value={assignment.eventType.label} />
        </Box>
      )}
      {assignment.eventType && <Divider />}

      {assignment.useCase && (
        <Box component="section">
          <Typography level="title-lg" mb={1}>
            Use Case
          </Typography>
          <Field label="Code" value={assignment.useCase.code} />
          {assignment.useCaseReasons && assignment.useCaseReasons.length > 0 && (
            <Box mt={1}>
              <Typography level="body-md" fontWeight="lg" mb={0.5}>
                Reasons:
              </Typography>
              {assignment.useCaseReasons.map((r, idx) => (
                <Box key={`reason-${idx}`} mb={0.5}>
                  <Field label="Reason Code" value={r.code} />
                  <OptionalField label="Detail" value={r.freeField} />
                </Box>
              ))}
            </Box>
          )}
        </Box>
      )}
      <Divider />

      {assignment.servicePoint && (
        <Box component="section">
          <Typography level="title-lg" mb={1}>
            Service Point
          </Typography>
          <Field label="Code" value={assignment.servicePoint.servicePointCode} />
          <Field label="Name" value={assignment.servicePoint.name} />
          {assignment.servicePoint.type && (
            <Field label="Type" value={assignment.servicePoint.type.code} />
          )}
          {assignment.servicePoint.logisticsMode && (
            <Field label="Logistics Mode" value={assignment.servicePoint.logisticsMode.code} />
          )}
          {assignment.servicePoint.gpsCoordinates && (
            <Box mt={1}>
              <Typography level="body-md" fontWeight="lg">
                GPS Coordinates:
              </Typography>
              <Field label="Latitude" value={assignment.servicePoint.gpsCoordinates.latitude} />
              <Field label="Longitude" value={assignment.servicePoint.gpsCoordinates.longitude} />
            </Box>
          )}
          {assignment.servicePoint.postalAddress && (
            <Box mt={1}>
              <Typography level="body-md" fontWeight="lg">
                Postal Address:
              </Typography>
              <RenderPostalAddress address={assignment.servicePoint.postalAddress} />
            </Box>
          )}
          {assignment.servicePoint.servicePointPostalAddress && (
            <Box mt={1}>
              <Typography level="body-md" fontWeight="lg">
                Service Point Address:
              </Typography>
              <RenderPostalAddress address={assignment.servicePoint.servicePointPostalAddress} />
            </Box>
          )}
        </Box>
      )}
      <Divider />

      {damagedObject && (
        <Box component="section">
          <Typography level="title-lg" mb={1}>
            Damaged Object
          </Typography>

          {damagedObject.manufacturedObjectSpecification && (
            <Box mt={1}>
              <Typography level="body-md" fontWeight="lg">
                Specifications:
              </Typography>
              <Field
                label="Product Family"
                value={damagedObject.manufacturedObjectSpecification?.productFamily?.code}
              />
              <Field
                label="Product Type"
                value={damagedObject.manufacturedObjectSpecification?.type?.code}
              />
            </Box>
          )}

          <Field label="Brand" value={damagedObject.marketBrandName} />
          <Field label="Model" value={damagedObject.modelName} />
          <OptionalField label="EAN" value={damagedObject.europeanArticleNumberingCode} />
          <OptionalField label="Serial No." value={damagedObject.serialNumber} />
          <OptionalField label="Purchase Date" value={damagedObject.purchaseDate} />
          {damagedObject.purchasePrice && (
            <Box mt={1}>
              <Typography level="body-md" fontWeight="lg">
                Purchase Price:
              </Typography>
              <Field
                label="Amount"
                value={damagedObject.purchasePrice.amount || damagedObject.purchasePrice.value}
              />
              <Field label="Currency" value={damagedObject.purchasePrice.currency} />
            </Box>
          )}
          {damagedObject.replacementValue && (
            <Box mt={1}>
              <Typography level="body-md" fontWeight="lg">
                Replacement Value:
              </Typography>
              <Field
                label="Amount"
                value={
                  damagedObject.replacementValue.amount || damagedObject.replacementValue.value
                }
              />
              <Field label="Currency" value={damagedObject.replacementValue.currency} />
            </Box>
          )}

          {damagedObject.characteristicsOfManufacturedObject && (
            <Box mt={1}>
              <Typography level="body-md" fontWeight="lg">
                Characteristics:
              </Typography>
              {damagedObject.characteristicsOfManufacturedObject.map((char, idx) => (
                <Box key={idx} mt={0.5}>
                  <Field label="Type" value={char.characteristicsType} />
                  {char.basedOnObjectSpecification && (
                    <Field label="Specification" value={char.basedOnObjectSpecification.code} />
                  )}
                </Box>
              ))}
            </Box>
          )}

          {damagedObject.additionalCharacteristics &&
            damagedObject.additionalCharacteristics.length > 0 && (
              <Box mt={1}>
                <Typography level="body-md" fontWeight="lg">
                  Additional Characteristics:
                </Typography>
                {damagedObject.additionalCharacteristics.map((char, idx) => (
                  <Box key={idx}>
                    <Box mt={0.5}>
                      <Field label="Type" value={char.additionalCharacteristicsType} />
                      {char.basedOnAdditionalSpecification && (
                        <Field
                          label={char.basedOnAdditionalSpecification.key}
                          value={char.basedOnAdditionalSpecification.value}
                        />
                      )}
                    </Box>
                    {idx < (damagedObject?.additionalCharacteristics?.length || 0) - 1 && (
                      <Divider
                        sx={{
                          borderWidth: "1px",
                          borderColor: "divider",
                          width: "60%",
                          marginBottom: "20px",
                        }}
                      />
                    )}
                  </Box>
                ))}
              </Box>
            )}
        </Box>
      )}
      <Divider />

      {assignment.committedCoverage && (
        <Box component="section">
          <Typography level="title-lg" mb={1}>
            Committed Coverage
          </Typography>
          <OptionalField
            label="Option Code"
            value={assignment.committedCoverage.coverageOption?.code}
          />
          <OptionalField
            label="Category"
            value={assignment.committedCoverage.coverageOptionCategory}
          />
          <OptionalField
            label="Specification Code"
            value={assignment.committedCoverage.coverageSpecification?.code}
          />
        </Box>
      )}
      <Divider />

      <Box component="section">
        <Typography level="title-lg" mb={1}>
          Customer
        </Typography>
        <Field label="Type" value={assignment.customer.customerType} />

        {assignment.customer.customerType === "PersonRef" && (
          <Box mt={1}>
            <Typography level="body-md" fontWeight="lg">
              Person:
            </Typography>
            <Field
              label="Full Name"
              value={`${(assignment.customer as any).personName?.prefixTitle?.code || ""} ${(assignment.customer as any).personName?.firstName || ""} ${(assignment.customer as any).personName?.lastName || ""}`.trim()}
            />
          </Box>
        )}

        {assignment.customer.customerType === "Organization" && (
          <Box mt={1}>
            <Typography level="body-md" fontWeight="lg">
              Organization:
            </Typography>
            <Field label="Name" value={(assignment.customer as any).name} />
            <OptionalField label="Legal Status" value={(assignment.customer as any).legalStatus} />
          </Box>
        )}

        {assignment.customer.contactInformation && (
          <Box mt={1}>
            <Typography level="body-md" fontWeight="lg">
              Contact:
            </Typography>
            {assignment.customer.contactInformation.emails?.map((email, idx) => (
              <Field key={`email-${idx}`} label="Email" value={email.email} />
            ))}
            {assignment.customer.contactInformation.cellPhones?.map((phone, idx) => (
              <Field key={`cell-${idx}`} label="Mobile" value={phone.phone || phone.cellPhone} />
            ))}
          </Box>
        )}
      </Box>
      <Divider />

      <Box component="section">
        <Typography level="title-lg" mb={1}>
          Customer Use Address
        </Typography>
        <RenderPostalAddress address={assignment.customerUseAddress} />
      </Box>
      <Divider />

      {assignment.pickUpAddress && (
        <Box component="section">
          <Typography level="title-lg" mb={1}>
            Pick Up Address
          </Typography>
          <RenderPostalAddress address={assignment.pickUpAddress} />
        </Box>
      )}
      {assignment.pickUpAddress && <Divider />}
    </Box>
  );
};

export default CommonReadOnlyLayout;

interface FieldProps {
  label: string;
  value: any;
}

function Field({ label, value }: FieldProps) {
  const finalValue = value !== undefined && value !== null && value !== "" ? String(value) : "-";
  return (
    <Grid container spacing={1} sx={{ mb: 0.5 }}>
      <Grid xs={4} sm={4} md={3}>
        <Typography level="body-sm" fontWeight="lg" textAlign="right">
          {label} :
        </Typography>
      </Grid>
      <Grid xs={8} sm={8} md={9}>
        <Typography level="body-md">{finalValue}</Typography>
      </Grid>
    </Grid>
  );
}

function OptionalField({ label, value }: FieldProps) {
  if (value === undefined || value === null || value === "") {
    return null;
  }
  return <Field label={label} value={value} />;
}

interface RenderPostalAddressProps {
  address: PostalAddress;
}

function RenderPostalAddress({ address }: RenderPostalAddressProps) {
  if (!address) return null;
  return (
    <Box>
      <OptionalField label="Recipient" value={address.recipientNameLine} />
      <OptionalField label="Complement" value={address.recipientComplementLine} />
      <OptionalField label="Building" value={address.buildingLine} />
      <Field
        label="Street"
        value={
          `${address.streetLine || address.street || ""}${address.streetNumber ? " " + address.streetNumber : ""}`.trim() ||
          null
        }
      />
      <OptionalField label="Locality" value={address.localityLine} />
      <OptionalField label="Postal Box" value={address.postalBoxLine} />
      <Field
        label="City/Zip Code"
        value={
          `${address.postalCode || ""} ${address.cityLine || address.city || ""}`.trim() || null
        }
      />
      <OptionalField label="State/Province" value={address.state} />
      <Field
        label="Country"
        value={
          address.countryLine?.label ||
          address.country?.label ||
          address.countryLine?.code ||
          address.country?.code ||
          null
        }
      />
    </Box>
  );
}
